import { useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ArrowLeft, Edit, Phone, Mail, MapPin, Briefcase, Calendar, Home, Wallet, Save, X, TrendingUp, CreditCard, AlertCircle, FileText, Download } from 'lucide-react';
import { Button, Card, CardStats, Table, StatusBadge, ExportDropdown, DocumentUpload } from '@components/ui';
import { useExportExcel, useExportPDF } from '@hooks';
import { getCustomerById } from '@mocks/musteriler';
import { mockChecks } from '@mocks/cekler';

export default function CustomerDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [isEditing, setIsEditing] = useState(false);
    const [documents, setDocuments] = useState([
        { id: 'doc-1', name: 'Satis_Sozlesmesi.pdf', type: 'application/pdf', size: 2048000, date: '2025-10-01' }
    ]);

    const individual = getCustomerById(id);

    const [formData, setFormData] = useState({
        name: individual?.name || '',
        phone: individual?.phone || '',
        email: individual?.email || '',
        tcNo: individual?.tcNo || '',
        address: individual?.address || '',
        occupation: individual?.occupation || '',
        workplace: individual?.workplace || '',
        notes: individual?.notes || '',
    });

    if (!individual) {
        return (
            <div className="p-6 md:p-12 flex flex-col items-center justify-center min-h-[60vh]">
                <div className="w-24 h-24 bg-bg-tertiary rounded-full flex items-center justify-center mb-6">
                    <AlertCircle size={48} className="text-text-muted" />
                </div>
                <h2 className="text-2xl font-bold text-text-primary mb-3">Müşteri Kaydı Bulunamadı</h2>
                <p className="text-text-tertiary text-center max-w-md mb-8 leading-relaxed">
                    Sistemde aradığınız ID'ye sahip bir müşteri kaydı bulunmuyor. Bu kayıt silinmiş veya hatalı bir bağlantıya tıklamış olabilirsiniz.
                </p>
                <Button
                    variant="primary"
                    onClick={() => navigate('/finans/musteriler')}
                    icon={<ArrowLeft size={18} />}
                    className="px-6 py-2.5"
                >
                    Müşteri Listesine Dön
                </Button>
            </div>
        );
    }

    // Export
    const reportRef = useRef(null);
    const { exportToExcel, isExporting: isExportingExcel } = useExportExcel();
    const { exportToPDF, isExporting: isExportingPDF } = useExportPDF();

    const handleExportExcel = () => {
        exportToExcel({
            data: (individual.payments || []).map(p => ({
                'Tarih': p.date,
                'Tip': p.type,
                'Tutar': p.amount,
                'Açıklama': p.description,
            })),
            columns: [
                { header: 'Tarih', key: 'Tarih' },
                { header: 'Tip', key: 'Tip' },
                { header: 'Tutar', key: 'Tutar', format: 'currency' },
                { header: 'Açıklama', key: 'Açıklama' },
            ],
            fileName: `musteri_${individual.name}`,
            sheetName: 'Ödemeler',
        });
    };

    const handleExportPDF = () => {
        exportToPDF({
            elementRef: reportRef,
            fileName: `musteri_${individual.name}`,
            title: `Müşteri Kartı - ${individual.name}`,
            orientation: 'portrait',
        });
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('tr-TR', {
            style: 'currency',
            currency: 'TRY',
            minimumFractionDigits: 0,
        }).format(amount);
    };

    const formatDate = (dateStr) => {
        return new Date(dateStr).toLocaleDateString('tr-TR', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        });
    };

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleSave = () => {
        console.log('Kaydedildi:', formData);
        toast.success('Kişi bilgileri güncellendi');
        setIsEditing(false);
    };

    const handleDocumentUpload = (file) => {
        // Mock upload
        const newDoc = {
            id: `doc-${Date.now()}`,
            name: file.name,
            type: file.type,
            size: file.size,
            date: new Date().toISOString().split('T')[0]
        };
        setDocuments(prev => [...prev, newDoc]);
        toast.success(`${file.name} başarıyla yüklendi.`);
    };

    const documentColumns = [
        {
            key: 'name', header: 'Belge Adı', width: 'lg',
            render: (val, row) => (
                <div className="flex items-center gap-3">
                    <FileText size={18} className={row.type.includes('pdf') ? "text-error" : "text-primary"} />
                    <span className="font-medium text-text-primary">{val}</span>
                </div>
            )
        },
        { key: 'date', header: 'Yüklenme Tarihi', width: 'md', render: (val) => formatDate(val) },
        { key: 'size', header: 'Boyut', width: 'sm', render: (val) => `${(val / 1024 / 1024).toFixed(2)} MB` },
        {
            key: 'actions', header: '', width: 'sm', align: 'right',
            render: () => (
                <Button variant="ghost" size="xs" icon={<Download size={16} />}>İndir</Button>
            )
        },
    ];

    const customerChecks = mockChecks.filter(c => c.customerId === individual?.id || c.customer === individual?.name);

    const checkColumns = [
        { key: 'checkNo', header: 'Çek No', width: 'md', render: (val) => <span className="font-mono text-sm font-medium">{val}</span> },
        { key: 'dueDate', header: 'Vade', width: 'md', render: (val) => formatDate(val) },
        {
            key: 'type', header: 'Tür', width: 'sm', render: (val) => (
                <StatusBadge status={val === 'verilen' ? 'Verilen' : 'Alınan'} variant={val === 'verilen' ? 'error' : 'success'} />
            )
        },
        { key: 'amount', header: 'Tutar', width: 'md', align: 'right', render: (val) => <span className="font-bold">{formatCurrency(val)}</span> },
        {
            key: 'status', header: 'Durum', width: 'sm', render: (val) => {
                const labels = { bekliyor: 'Bekliyor', odendi: 'Ödendi', tahsil: 'Tahsil Edildi', ciro_edildi: 'Ciro Edildi', iade: 'İade', gecikti: 'Gecikti' };
                const variants = { bekliyor: 'warning', odendi: 'primary', tahsil: 'success', ciro_edildi: 'info', iade: 'neutral', gecikti: 'error' };
                return <StatusBadge status={labels[val] || val} variant={variants[val] || 'default'} />;
            }
        }
    ];

    const paymentColumns = [
        { key: 'date', header: 'Tarih', width: 'md', render: (val) => formatDate(val) },
        {
            key: 'type', header: 'Tip', width: 'sm', render: (val) => (
                <StatusBadge status={val} variant={val === 'pesin' ? 'success' : val === 'kapora' ? 'warning' : 'info'} />
            )
        },
        {
            key: 'amount', header: 'Tutar', width: 'md', align: 'right', render: (val) => (
                <span className="text-success font-medium">{formatCurrency(val)}</span>
            )
        },
        { key: 'description', header: 'Açıklama', width: 'lg' },
    ];

    const apartmentColumns = [
        { key: 'number', header: 'Daire No', width: 'sm', render: (val) => <span className="font-medium">{val}</span> },
        { key: 'constructionName', header: 'İnşaat', width: 'lg' },
        {
            key: 'status', header: 'Durum', width: 'sm', render: (val) => (
                <StatusBadge status={val === 'sold' ? 'Satıldı' : 'Rezerve'} variant={val === 'sold' ? 'success' : 'warning'} />
            )
        },
        { key: 'price', header: 'Fiyat', width: 'md', align: 'right', render: (val) => formatCurrency(val) },
    ];

    // Mobile Payment Card View
    const PaymentCardView = () => (
        <div className="space-y-3 md:hidden">
            {individual.payments?.map((payment) => (
                <div key={payment.id} className="bg-bg-tertiary rounded-lg p-4">
                    <div className="flex items-center justify-between gap-3 mb-2">
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-success-100 flex items-center justify-center">
                                <TrendingUp size={16} className="text-success" />
                            </div>
                            <div>
                                <p className="text-sm font-medium text-text-primary">{formatDate(payment.date)}</p>
                                <StatusBadge status={payment.type} variant={payment.type === 'pesin' ? 'success' : payment.type === 'kapora' ? 'warning' : 'info'} size="xs" />
                            </div>
                        </div>
                        <span className="text-success font-bold">{formatCurrency(payment.amount)}</span>
                    </div>
                    <p className="text-sm text-text-muted">{payment.description}</p>
                </div>
            ))}
            {(!individual.payments || individual.payments.length === 0) && (
                <p className="text-center text-text-tertiary py-4">Ödeme kaydı bulunamadı.</p>
            )}
        </div>
    );

    // Mobile Check Card View
    const CheckCardView = () => (
        <div className="space-y-3 md:hidden">
            {customerChecks.map((check) => {
                const statusLabels = { bekliyor: 'Bekliyor', odendi: 'Ödendi', tahsil: 'Tahsil Edildi', ciro_edildi: 'Ciro Edildi', iade: 'İade', gecikti: 'Gecikti' };
                const statusVariants = { bekliyor: 'warning', odendi: 'primary', tahsil: 'success', ciro_edildi: 'info', iade: 'neutral', gecikti: 'error' };
                return (
                    <div key={check.id} className="bg-bg-tertiary rounded-lg p-4">
                        <div className="flex items-start justify-between gap-3 mb-2">
                            <div className="min-w-0">
                                <p className="font-semibold text-text-primary truncate font-mono text-sm">{check.checkNo}</p>
                                <p className="text-xs text-text-tertiary">{formatDate(check.dueDate)}</p>
                            </div>
                            <div className="flex flex-col items-end gap-1">
                                <StatusBadge status={statusLabels[check.status] || check.status} variant={statusVariants[check.status] || 'default'} size="xs" />
                                <StatusBadge status={check.type === 'verilen' ? 'Verilen' : 'Alınan'} variant={check.type === 'verilen' ? 'error' : 'success'} size="xs" />
                            </div>
                        </div>
                        <div className="flex items-center justify-between text-sm mt-3 pt-3 border-t border-border-light">
                            <span className="text-text-muted text-xs truncate max-w-[120px]">{check.description || '-'}</span>
                            <span className="font-bold text-text-primary">{formatCurrency(check.amount)}</span>
                        </div>
                    </div>
                );
            })}
            {customerChecks.length === 0 && (
                <p className="text-center text-text-tertiary py-4">Kayıtlı çek bulunamadı.</p>
            )}
        </div>
    );

    // Mobile Apartment Card View
    const ApartmentCardView = () => (
        <div className="space-y-3 md:hidden">
            {individual.apartments?.map((apt) => (
                <div key={apt.id} className="bg-bg-tertiary rounded-lg p-4">
                    <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
                                <Home size={20} className="text-primary" />
                            </div>
                            <div>
                                <p className="font-semibold text-text-primary">{apt.number}</p>
                                <p className="text-xs text-text-tertiary">{apt.constructionName}</p>
                            </div>
                        </div>
                        <div className="text-right">
                            <p className="font-medium text-text-primary">{formatCurrency(apt.price)}</p>
                            <StatusBadge status={apt.status === 'sold' ? 'Satıldı' : 'Rezerve'} variant={apt.status === 'sold' ? 'success' : 'warning'} size="xs" />
                        </div>
                    </div>
                </div>
            ))}
            {(!individual.apartments || individual.apartments.length === 0) && (
                <p className="text-center text-text-tertiary py-4">Bağlı daire bulunamadı.</p>
            )}
        </div>
    );

    return (
        <div className="p-4 md:p-6 space-y-6" ref={reportRef}>
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-3 sm:gap-4">
                    <Button variant="ghost" onClick={() => navigate('/musteriler')} className="min-w-[44px] min-h-[44px] p-2">
                        <ArrowLeft size={20} />
                    </Button>
                    <div>
                        <h1 className="text-xl sm:text-2xl font-bold text-text-primary">{individual.name}</h1>
                        <p className="text-text-tertiary text-sm sm:text-base">{individual.roleLabel}</p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <ExportDropdown
                        onExportExcel={handleExportExcel}
                        onExportPDF={handleExportPDF}
                        isExporting={isExportingExcel || isExportingPDF}
                    />
                    {isEditing ? (
                        <>
                            <Button variant="ghost" onClick={() => setIsEditing(false)} icon={<X size={18} />} className="min-h-[44px]">
                                İptal
                            </Button>
                            <Button variant="success" onClick={handleSave} icon={<Save size={18} />} className="min-h-[44px]">
                                Kaydet
                            </Button>
                        </>
                    ) : (
                        <Button variant="primary" onClick={() => setIsEditing(true)} icon={<Edit size={18} />} className="min-h-[44px]">
                            Düzenle
                        </Button>
                    )}
                </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                <CardStats title="Toplam Ödenen" value={formatCurrency(individual.totalPaid)} icon={Wallet} color="success" size="sm" />
                <CardStats title="Kalan Borç" value={formatCurrency(individual.totalRemaining)} icon={CreditCard} color={individual.totalRemaining > 0 ? 'warning' : 'success'} size="sm" />
                <CardStats title="Daire Sayısı" value={individual.apartments?.length || 0} icon={Home} color="primary" size="sm" />
                <CardStats title="Ödeme Sayısı" value={individual.payments?.length || 0} icon={Calendar} color="tertiary" size="sm" />
            </div>

            {/* Info Card */}
            <Card padding="lg">
                <h3 className="text-lg font-semibold text-text-primary mb-4">Kişisel Bilgiler</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                    {isEditing ? (
                        <>
                            <div>
                                <label className="block text-sm font-medium text-text-secondary mb-1">Ad Soyad</label>
                                <input
                                    type="text" className="w-full px-4 py-3 rounded-lg border border-border-light bg-bg-primary text-base"
                                    value={formData.name} onChange={(e) => handleChange('name', e.target.value)}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-text-secondary mb-1">Telefon</label>
                                <input
                                    type="tel" className="w-full px-4 py-3 rounded-lg border border-border-light bg-bg-primary text-base"
                                    value={formData.phone} onChange={(e) => handleChange('phone', e.target.value)}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-text-secondary mb-1">E-posta</label>
                                <input
                                    type="email" className="w-full px-4 py-3 rounded-lg border border-border-light bg-bg-primary text-base"
                                    value={formData.email} onChange={(e) => handleChange('email', e.target.value)}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-text-secondary mb-1">TC Kimlik No</label>
                                <input
                                    type="text" className="w-full px-4 py-3 rounded-lg border border-border-light bg-bg-primary text-base"
                                    value={formData.tcNo} onChange={(e) => handleChange('tcNo', e.target.value)}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-text-secondary mb-1">Meslek</label>
                                <input
                                    type="text" className="w-full px-4 py-3 rounded-lg border border-border-light bg-bg-primary text-base"
                                    value={formData.occupation} onChange={(e) => handleChange('occupation', e.target.value)}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-text-secondary mb-1">İşyeri</label>
                                <input
                                    type="text" className="w-full px-4 py-3 rounded-lg border border-border-light bg-bg-primary text-base"
                                    value={formData.workplace} onChange={(e) => handleChange('workplace', e.target.value)}
                                />
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-text-secondary mb-1">Adres</label>
                                <input
                                    type="text" className="w-full px-4 py-3 rounded-lg border border-border-light bg-bg-primary text-base"
                                    value={formData.address} onChange={(e) => handleChange('address', e.target.value)}
                                />
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-text-secondary mb-1">Notlar</label>
                                <textarea
                                    className="w-full px-4 py-3 rounded-lg border border-border-light bg-bg-primary resize-none text-base"
                                    rows={3} value={formData.notes} onChange={(e) => handleChange('notes', e.target.value)}
                                />
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="flex items-center gap-3 p-3 bg-bg-tertiary rounded-lg">
                                <Phone size={18} className="text-text-muted shrink-0" />
                                <div className="min-w-0">
                                    <p className="text-xs text-text-tertiary">Telefon</p>
                                    <p className="text-text-primary truncate">{individual.phone}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3 p-3 bg-bg-tertiary rounded-lg">
                                <Mail size={18} className="text-text-muted shrink-0" />
                                <div className="min-w-0">
                                    <p className="text-xs text-text-tertiary">E-posta</p>
                                    <p className="text-text-primary truncate">{individual.email || '-'}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3 p-3 bg-bg-tertiary rounded-lg">
                                <Briefcase size={18} className="text-text-muted shrink-0" />
                                <div className="min-w-0">
                                    <p className="text-xs text-text-tertiary">Meslek</p>
                                    <p className="text-text-primary truncate">{individual.occupation} - {individual.workplace}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3 p-3 bg-bg-tertiary rounded-lg">
                                <FileText size={18} className="text-text-muted shrink-0" />
                                <div className="min-w-0">
                                    <p className="text-xs text-text-tertiary">TC Kimlik No</p>
                                    <p className="text-text-primary truncate">{individual.tcNo || '-'}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3 p-3 bg-bg-tertiary rounded-lg">
                                <MapPin size={18} className="text-text-muted shrink-0" />
                                <div className="min-w-0">
                                    <p className="text-xs text-text-tertiary">Adres</p>
                                    <p className="text-text-primary truncate">{individual.address}</p>
                                </div>
                            </div>
                            {individual.notes && (
                                <div className="md:col-span-2">
                                    <p className="text-sm text-text-tertiary mb-1">Notlar</p>
                                    <p className="text-text-primary bg-bg-tertiary p-3 rounded-lg">{individual.notes}</p>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </Card>

            {/* Apartments */}
            <Card padding="lg">
                <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
                    <Home size={20} /> Sahip Olduğu Daireler
                    <span className="text-sm font-normal text-text-tertiary">({individual.apartments?.length || 0})</span>
                </h3>
                {/* Mobile Card View */}
                <ApartmentCardView />
                {/* Desktop Table View */}
                <div className="hidden md:block">
                    <Table
                        columns={apartmentColumns}
                        data={individual.apartments || []}
                        compact
                        emptyMessage="Bağlı daire bulunamadı."
                        emptyIcon={<Home size={32} />}
                    />
                </div>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
                {/* Checks */}
                <Card padding="lg">
                    <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
                        <Wallet size={20} /> Çek Portföyü
                        <span className="text-sm font-normal text-text-tertiary">({customerChecks.length})</span>
                    </h3>
                    <CheckCardView />
                    <div className="hidden md:block">
                        <Table
                            columns={checkColumns}
                            data={customerChecks}
                            compact
                            emptyMessage="Kayıtlı çek bulunamadı."
                            emptyIcon={<Wallet size={32} />}
                        />
                    </div>
                </Card>

                {/* Payments */}
                <Card padding="lg">
                    <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
                        <Wallet size={20} /> Nakit Ödeme Geçmişi
                        <span className="text-sm font-normal text-text-tertiary">({individual.payments?.length || 0})</span>
                    </h3>
                    {/* Mobile Card View */}
                    <PaymentCardView />
                    {/* Desktop Table View */}
                    <div className="hidden md:block">
                        <Table
                            columns={paymentColumns}
                            data={individual.payments || []}
                            compact
                            emptyMessage="Ödeme kaydı bulunamadı."
                            emptyIcon={<Wallet size={32} />}
                        />
                    </div>
                </Card>
            </div>

            {/* Documents & Contracts */}
            <Card padding="lg">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                    <h3 className="text-lg font-semibold text-text-primary flex items-center gap-2">
                        <FileText size={20} /> Belgeler & Sözleşmeler
                        <span className="text-sm font-normal text-text-tertiary">({documents.length})</span>
                    </h3>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Upload Area */}
                    <div className="lg:col-span-1">
                        <DocumentUpload
                            onUpload={handleDocumentUpload}
                            title="Yeni Belge Yükle"
                            maxSizeMB={10}
                        />
                    </div>

                    {/* Documents List */}
                    <div className="lg:col-span-2">
                        {documents.length > 0 ? (
                            <Table
                                columns={documentColumns}
                                data={documents}
                                compact
                            />
                        ) : (
                            <div className="h-full min-h-[200px] flex items-center justify-center border-2 border-dashed border-border-light rounded-xl">
                                <p className="text-text-tertiary text-sm">Henüz belge yüklenmemiş.</p>
                            </div>
                        )}
                    </div>
                </div>
            </Card>
        </div>
    );
}

